import React, { useState, useEffect, useContext } from 'react';

import {
    Box,
    Button,
    Divider,
    Tooltip,
} from '@mui/material';

import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarExportContainer,
    ptBR,
} from '@mui/x-data-grid-pro';
import { ptBR as corePtBR } from '@mui/material/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { Dialog, DialogTitle } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import dayjs from 'dayjs';
import { writeFile, utils } from 'xlsx';
import { ListItem } from '@mui/material';

import '../../../styles.css';
import api from '../../../../../api';


const CampaignHistory = props => {

    const [loading, updateLoading] = useState(false)
    const [historyData, updateHistoryData] = useState([])

    const fetchHistoryData = async (id) => {
        updateLoading(true)
        const url = `/api/enterprises/${props.enterpriseId}/campaign/${id}/campaign_history/`;
        const response = await api.get(url)
        updateLoading(false)
        updateHistoryData(response.data)
    }

    useEffect(() => {
        updateHistoryData([])
        if (props.data.id && props.open)
            fetchHistoryData(props.data.id)
    }, [props.open])

    const getDateRange = (row) => {
        let startDate, endDate
        if (row instanceof Date) {
            startDate = new Date(row)
            startDate.setHours(0, 0, 0, 0)
            endDate = new Date(row)
            endDate.setHours(23, 59, 59, 0)
        } else {
            startDate = new Date(row.run_datetime)
            startDate.setHours(0, 0, 0, 0)
            startDate.setHours(startDate.getHours() - 3)
            endDate = new Date(row.run_datetime)
            endDate.setHours(23, 59, 59, 0)
            endDate.setHours(endDate.getHours() - 3)
        }
        return [startDate.toISOString().slice(0, 16), endDate.toISOString().slice(0, 16)]
    }

    const createHistoryColumns = () => {
        const isVoice = props.data?.platform === 'VOICE'
        const hasResent = historyData.some(row => row.resent_percentage !== null && row.resent_percentage > 0)
        
        const columns = [
                {
                    field: 'run_datetime', headerName: 'Data', flex: 1, type: 'dateTime',
                    valueGetter: ({ value }) => {
                        if (value) {
                            const date = new Date(value);
                            date.setHours(date.getHours() - 3);
                            return date;
                        }
                    },
                    renderCell: (params) => (
                        <Box>
                            {params.value ? params.value.toLocaleString('pt-BR') : ''}
                        </Box>
                    )
                },
                { field: 'amount', headerName: 'Quantidade', flex: 0.5 },
                {
                    field: 'success_percentage', headerName: isVoice ? 'Atendidas' : 'Enviadas', flex: 0.5,
                    renderCell: (params) => (
                        <Box>
                            {params.value}%
                        </Box>
                    )
                },
                {
                    field: 'read_percentage', headerName: 'Lidas', flex: 0.5, hide: isVoice,
                    renderCell: (params) => (
                        <Box>
                            {params.value}%
                        </Box>
                    )
                },
                {
                    field: 'used_percentage', headerName: isVoice ? 'Não Atendidas' : 'Respondidas', flex: 0.5,
                    renderCell: (params) => (
                        <Box>
                            {params.value}%
                        </Box>
                    )
                },
                {
                    field: 'pending_percentage', headerName: 'Pendentes', flex: 0.5,
                    renderCell: (params) => (
                        <Box>
                            {params.value}%
                        </Box>
                    )
                },
                {
                    field: 'notsent_percentage', headerName: 'Não Enviadas', flex: 0.5,
                    hide: isVoice,
                    renderCell: (params) => (
                        <Box>
                            {params.value}%
                        </Box>
                    )
                },
                {
                    field: 'resent', headerName: 'Ações', flex: 0.5,
                    renderCell: (params) => (
                        <Box>
                            <Tooltip title="Ver disparos" placement="top" disableInteractive>
                                <Button className="action-button"
                                    onClick={() => props.setupNotification(props.data.id, getDateRange(params.row))}>
                                    <MessageIcon />
                                </Button>
                            </Tooltip>
                        </Box>
                    )
                },
            ]
        
        if (hasResent) {
            columns.splice(6, 0, {
                field: 'resent_percentage', headerName: 'Reenviadas', flex: 0.5,
                renderCell: (params) => (
                    <Box>
                        {params.value}%
                    </Box>
                )
            })
        }
        
        return columns
    }

    const theme = createTheme({
        palette: {
            primary: { main: '#21446C' },
        },
    }, ptBR, corePtBR);

    const exportHistory = (format) => {
        if (historyData.length === 0) return;
        const formatted = historyData.map(row => {
            const isVoice = props.data?.platform === 'VOICE'
            const base = {
                Data: row.run_datetime ? new Date(new Date(row.run_datetime) - 3 * 3600000).toLocaleString() : '',
                Quantidade: row.amount,
                [isVoice ? 'Atendidas' : 'Sucesso']: row.success_percentage + '%',
                [isVoice ? 'Não atendidas' : 'Resposta']: row.used_percentage + '%',
                Pendentes: row.pending_percentage + '%',
            };
            if (!isVoice)
                base['Não enviadas'] = row.notsent_percentage + '%'
            if (row.resent_percentage !== null && row.resent_percentage !== undefined) {
                base['Reenviadas'] = row.resent_percentage + '%';
            }
            return base;
        });
        const workbook = utils.book_new();
        const worksheet = utils.json_to_sheet(formatted);

        const campaignName = props.data?.name || 'campanha';
        const safeName = campaignName.replace(/\W+/g, '_');
        const timestamp = dayjs().format('DD-MM-YY-HH-mm-ss');
        const baseFilename = `Campanha_${safeName}-${timestamp}`;

        if (format === 'csv') {
            const csv = utils.sheet_to_csv(worksheet);
            const bom = '\uFEFF';
            const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', `${baseFilename}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            utils.book_append_sheet(workbook, worksheet, 'Histórico');
            writeFile(workbook, `${baseFilename}.xlsx`, { compression: true });
        }
    };

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExportContainer>
                    <ListItem onClick={() => exportHistory('csv')} className={'menuItem'}>
                        Exportar como CSV
                    </ListItem>
                    <ListItem onClick={() => exportHistory('xlsx')} className={'menuItem'}>
                        Exportar como XLSX
                    </ListItem>
                </GridToolbarExportContainer>
            </GridToolbarContainer>
        )
    }

    return (
        <div>
            {
                <Dialog keepMounted
                    open={props.open}
                    onClose={props.handleClose}
                    aria-labelledby="customized-dialog-title"
                    fullWidth
                    disablePortal
                    disableEnforceFocus
                    sx={{position:'absolute'}}
                    maxWidth="lg"
                >
                    <DialogTitle id="customized-dialog-title" className={'group-flexbox'}>
                        <span>{props.title}: {props.data.name}</span>
                        <Tooltip title="Fechar" >
                            <Button className="action-button"  >
                                <CloseIcon onClick={props.handleClose} />
                            </Button>
                        </Tooltip>
                    </DialogTitle>
                    <Divider />
                    <div className='tableWrapper'>
                        <Box sx={{ width: '100%', height: '520px' }}>
                            <ThemeProvider theme={theme}>
                                <DataGridPro
                                    columns={createHistoryColumns()}
                                    rows={historyData}
                                    loading={loading}
                                    disableSelectionOnClick
                                    components={{ Toolbar: CustomToolbar }}
                                    density={'compact'}
                                />
                            </ThemeProvider>
                        </Box>
                    </div>
                </Dialog>
            }
        </div>

    );
}

export default CampaignHistory
